/**
 * Utilitários para geração LaTeX
 */

/**
 * Escapa caracteres especiais do LaTeX
 * Melhorado para preservar strings base64 e URLs de imagem sem alteração
 */
export function escapeLatex(text: string): string {
  if (!text) return '';

  const trimmed = text.trim();

  // Detectar se é uma string base64 (pode começar com "data:image" ou conter padrão base64)
  const base64Pattern = /^data:image\/\w+;base64,/;
  const isBase64 = base64Pattern.test(trimmed) ||
    (text.length > 100 && text.includes('data:image') && text.includes('base64,'));

  // Para URLs base64, não escapar - o backend vai substituir por nome de arquivo
  if (isBase64) {
    return text;
  }

  // URLs de imagem (/uploads/ ou http(s):) não devem ser escapadas - o backend resolve e substitui
  // Se escaparmos { e }, o backend não reconhece o padrão \includegraphics{url}
  if (trimmed.startsWith('/uploads/') || trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
    return text;
  }

  // Se for uma string muito longa (não base64), pode ser um problema
  // Mas vamos permitir e deixar o backend lidar
  if (text.length > 10000 && !text.startsWith('data:')) {
    // Truncar strings muito longas que não são base64
    console.warn('[WARN] String muito longa truncada (não é base64):', text.length, 'chars');
    return text.substring(0, 10000);
  }

  return text
    .replace(/\\/g, '\\textbackslash{}')
    .replace(/\{/g, '\\{')
    .replace(/\}/g, '\\}')
    .replace(/\$/g, '\\$')
    .replace(/\&/g, '\\&')
    .replace(/#/g, '\\#')
    .replace(/\^/g, '\\textasciicircum{}')
    .replace(/_/g, '\\_')
    .replace(/%/g, '\\%')
    .replace(/~/g, '\\textasciitilde{}');
}

/**
 * Escapa caracteres especiais do LaTeX preservando quebras de linha
 * Converte \n em \newline (comando LaTeX para quebra de linha em células de tabela)
 * Melhorado para preservar strings base64 completas
 */
export function escapeLatexComQuebras(text: string): string {
  if (!text) return '';

  const trimmed = text.trim();
  const base64Pattern = /^data:image\/\w+;base64,/;
  const isBase64 = base64Pattern.test(trimmed) ||
    (text.length > 100 && text.includes('data:image') && text.includes('base64,'));

  if (isBase64) {
    return text;
  }
  if (trimmed.startsWith('/uploads/') || trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
    return text;
  }

  // Dividir por quebras de linha, escapar cada linha e juntar com \newline
  // \newline funciona melhor em células de tabela do que \\
  const linhas = text.split('\n');
  const linhasEscapadas = linhas
    .map(linha => escapeLatex(linha))
    .filter((linha, index) => {
      // Preservar todas as linhas, mas remover linhas vazias no final
      if (index === linhas.length - 1 && linha === '') {
        return false;
      }
      return true;
    });

  return linhasEscapadas.join('\\newline ');
}

/**
 * Escapa caracteres especiais do LaTeX preservando comandos LaTeX válidos
 * Detecta e preserva comandos como \begin{...}, \end{...}, \item, \textbf{...}, etc.
 * @param text Texto a ser escapado
 * @returns Texto com caracteres especiais escapados, mas comandos LaTeX preservados
 */
export function escapeLatexPreservandoComandos(text: string): string {
  if (!text) return '';

  const trimmed = text.trim();
  const base64Pattern = /^data:image\/\w+;base64,/;
  const isBase64 = base64Pattern.test(trimmed) ||
    (text.length > 100 && text.includes('data:image') && text.includes('base64,'));

  if (isBase64) {
    return text;
  }
  if (trimmed.startsWith('/uploads/') || trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
    return text;
  }

  // Se for uma string muito longa (não base64), pode ser um problema
  if (text.length > 10000 && !text.startsWith('data:')) {
    return text.substring(0, 10000);
  }

  // Se o texto já contém comandos LaTeX estruturados (como \begin{...}...\end{...}),
  // não escapar {, }, \ - assumir que já está formatado corretamente
  // Apenas escapar caracteres especiais que não fazem parte de comandos LaTeX
  if (text.includes('\\begin{') || text.includes('\\end{') || text.includes('\\item')) {
    // Escapar apenas caracteres especiais que não fazem parte de comandos LaTeX
    // Não escapar \, {, } pois podem fazer parte de comandos LaTeX válidos
    return text
      .replace(/\$/g, '\\$')
      .replace(/\&/g, '\\&')
      .replace(/#/g, '\\#')
      .replace(/%/g, '\\%')
      .replace(/~/g, '\\textasciitilde{}');
  }

  // Se não contém comandos LaTeX, usar escape normal
  return escapeLatex(text);
}


